import toast from "react-hot-toast";
import { AxiosPaymentInstance } from "./AxiosInstance";
import { cashfreeCheckout } from "./cashfreePayment";
import { serviceCheckout } from "../../redux/features/documentService/documentServiceActions";
import { formatRupees } from "./formatRupees";
import { msg } from "./messages";

// Part-credit, part-cash checkout. When the wallet is short, the order is
// sent with `payShortfall: true`: the backend uses whatever credits the
// customer has and returns a Cashfree session for the rest. That cash pays for
// this order only — it never goes into the wallet. The credits are taken and
// the order is placed once the payment is confirmed.

const POLL_EVERY_MS = 3000;
const POLL_TRIES = 10;

const authHeaders = () => ({
  "x-auth-token": JSON.parse(localStorage.getItem("token")),
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * The backend's one-line reason a `serviceCheckout` failed. The thunk rejects
 * with the response's `status` object as `message`; the fallback is only for
 * no response at all (network down).
 */
export const checkoutError = (res) =>
  res?.payload?.message?.message || msg("ORDER.FAILED");

/** What an order will cost from a given balance. 1 credit = ₹1. */
export function splitPrice(price, balance) {
  const usable = Math.max(0, Math.min(Number(balance) || 0, price));
  const amount = Math.max(1, Math.round((price - usable) * 100) / 100);
  return {
    creditsApplied: Math.max(0, Math.round((price - amount) * 100) / 100),
    amount,
  };
}

/**
 * Button text for a short wallet: "Pay ₹179/- (₹120/- from wallet)", or
 * "Pay ₹299/-" when the wallet is empty. Order screens show money, never
 * credits (1 credit = ₹1).
 */
export function shortfallLabel(price, balance) {
  const { creditsApplied, amount } = splitPrice(price, balance);
  return creditsApplied > 0
    ? `Pay ${formatRupees(amount)} (${formatRupees(creditsApplied)} from wallet)`
    : `Pay ${formatRupees(amount)}`;
}

async function verify(orderId) {
  const res = await AxiosPaymentInstance.post(
    `/v1/customer/checkout/verify/${orderId}`,
    {},
    { headers: authHeaders() }
  );
  return res?.data?.data?.item?.status;
}

/**
 * Opens Cashfree for a shortfall checkout response and confirms the payment.
 * Resolves "PAID" | "PENDING" | "FAILED". PENDING means Cashfree hasn't
 * confirmed yet — the webhook will still place the order when it does.
 *
 * @param {{sessionId: string, orderId: string}} checkout the checkout response item
 */
export async function payShortfall({ sessionId, orderId }) {
  let result;
  try {
    result = await cashfreeCheckout(
      {
        paymentSessionId: sessionId,
        returnUrl: window.location.href,
        redirect: true,
        redirectTarget: "_modal",
      },
      { silent: true }
    );
  } catch (e) {
    // The SDK failed to load or open — nothing was paid.
    return "FAILED";
  }
  if (result?.error) {
    // Closed or failed in the sheet. A UPI payment approved on the phone can
    // still land after the sheet closes, so ask once before calling it failed;
    // if it lands later still, the backend places the order on its own.
    try {
      if ((await verify(orderId)) === "PAID") return "PAID";
    } catch (e) {
      // fall through
    }
    return "FAILED";
  }

  for (let i = 0; i < POLL_TRIES; i++) {
    try {
      const status = await verify(orderId);
      if (status === "PAID" || status === "FAILED") return status;
    } catch (e) {
      // network blip — keep polling
    }
    await sleep(POLL_EVERY_MS);
  }
  return "PENDING";
}

/**
 * Places a checkout-endpoint order when the wallet is short: sends it with
 * `payShortfall`, opens Cashfree for the rest and confirms the payment.
 * Resolves { status: "PAID" | "PENDING" | "FAILED" | "ERROR", orderId, message }.
 */
export async function checkoutWithShortfall(dispatch, { body, type }) {
  const res = await dispatch(
    serviceCheckout({ body: { ...body, payShortfall: true }, type })
  );
  const item = res?.payload?.response?.data?.data?.item;
  if (res?.payload?.response?.status !== 200) {
    return { status: "ERROR", message: checkoutError(res) };
  }
  // No session: the balance covered it after all (topped up in another tab),
  // so the order is already placed.
  if (!item?.sessionId) return { status: "PAID", orderId: item?.orderId };
  const status = await payShortfall(item);
  return { status, orderId: item.orderId };
}

/** Toasts the outcome; returns true when the order went (or is going) through. */
export function notifyShortfallResult({ status, message }) {
  if (status === "ERROR") {
    toast.error(message || msg("ORDER.FAILED"));
    return false;
  }
  // Cashfree's own outcome, not an API error — kept to one line like the rest.
  // A debited payment still places the order once it confirms.
  if (status === "FAILED") {
    toast.error(msg("PAYMENT.NOT_COMPLETED"));
    return false;
  }
  if (status === "PENDING") {
    toast(msg("PAYMENT.CONFIRMING"));
  }
  return true;
}
